import * as React from 'react';

import { useUpdatedRef } from '@hooks/useUpdatedRef';

import { useReviewFromCache } from '../../hooks';
import { useReviewPhotoModalStore } from '../../store';

export const useGalleryKeyboardControls = ({
  sliderRef,
  showNextReviewArrow,
  showPrevReviewArrow,
  onNextReviewClick,
  onPrevReviewClick,
}: {
  sliderRef: React.MutableRefObject<{
    setCurrentSlide: (slideIndex: number, behavior?: ScrollBehavior) => void;
  } | null>;
  onNextReviewClick?: () => void;
  onPrevReviewClick?: () => void;
  showNextReviewArrow?: boolean;
  showPrevReviewArrow?: boolean;
}) => {
  const selectedImage = useReviewPhotoModalStore((x) => x.selectedImage);
  const reviewId = useReviewPhotoModalStore((x) => x.reviewId);
  const review = useReviewFromCache(reviewId);
  const images = review?.images ?? [];

  const selectedImageRef = useUpdatedRef(selectedImage);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const currIndex: number = images.findIndex(
        (i) => i.fullSizeUrl === selectedImageRef.current.fullSizeUrl
      );
      const isFirstImage = currIndex === 0;
      const isLastImage = currIndex === images.length - 1;

      const handleArrowLeft = () => {
        const shouldNavigateToPrevReview = isFirstImage && showPrevReviewArrow;

        if (shouldNavigateToPrevReview) {
          onPrevReviewClick?.();
        } else {
          sliderRef.current?.setCurrentSlide(Math.max(currIndex - 1, 0));
        }
      };

      const handleArrowRight = () => {
        const shouldNavigateToNextReview = isLastImage && showNextReviewArrow;

        if (shouldNavigateToNextReview) {
          onNextReviewClick?.();
        } else {
          sliderRef.current?.setCurrentSlide(
            Math.min(currIndex + 1, images.length - 1)
          );
        }
      };

      if (event.key === 'ArrowLeft') {
        handleArrowLeft();
      } else if (event.key === 'ArrowRight') {
        handleArrowRight();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    sliderRef,
    images,
    selectedImageRef,
    onNextReviewClick,
    onPrevReviewClick,
    showNextReviewArrow,
    showPrevReviewArrow,
  ]);
};
